
import { Component, Vue } from 'vue-property-decorator'
import { getToken } from '@/utils/cookies'
import request from '@/utils/request'
import { getPurchaseStats } from '@/api/purchases'
import { getUserById } from '@/api/users'
import { getUserEntitlements, Entitlement } from '@/api/entitlements'
import UserSearch from '@/components/UserSearch/index.vue'

@Component({
  name: 'SubscriptionEvents',
  components: {
    UserSearch
  }
})
export default class extends Vue {
  private listLoading = false
  private events: any[] = []
  private total = 0
  private statistics: any[] = []
  private startDate = ''
  private endDate = ''
  private detailsDialogVisible = false
  private selectedEvent: any = {}

  private userDetailVisible = false
  private userDetailLoading = false
  private userDetailData: any = {}
  private userEntitlements: Entitlement[] = []
  private entitlementsLoading = false
  private userDetailTab = 'basic'

  private purchaseStats = {
    total: 0,
    active: 0,
    inactive: 0,
    thisWeek: 0,
    thisMonth: 0
  }

  private listQuery = {
    page: 1,
    limit: 50,
    platform: '',
    eventType: '',
    status: '',
    userId: '',
    includeTest: false
  }

  created() {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 7)
    this.startDate = start.toISOString().split('T')[0]
    this.endDate = end.toISOString().split('T')[0]
    this.loadEvents()
    this.loadPurchaseStats()
  }

  private async loadPurchaseStats() {
    try {
      const { data } = await getPurchaseStats()
      if (data && data.data && data.data.summary) {
        this.purchaseStats = {
          total: data.data.summary.total || 0,
          active: data.data.summary.active || 0,
          inactive: data.data.summary.inactive || 0,
          thisWeek: data.data.summary.thisWeek || 0,
          thisMonth: data.data.summary.thisMonth || 0
        }
      }
    } catch (error) {
      console.error('Failed to load purchase stats:', error)
    }
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return '-'
    const date = new Date(dateStr)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${day}.${month}.${year} ${hours}:${minutes}`
  }

  private formatDateShort(dateStr: string): string {
    if (!dateStr) return '-'
    const date = new Date(dateStr)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    return `${day}.${month}.${year}`
  }

  private formatTime(dateStr: string): string {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${hours}:${minutes}`
  }

  private async loadEvents() {
    this.listLoading = true
    try {
      const params: any = {
        page: this.listQuery.page,
        limit: this.listQuery.limit
      }

      if (this.startDate) {
        params.startDate = this.startDate
      }
      if (this.endDate) {
        params.endDate = this.endDate
      }
      if (this.listQuery.platform) params.platform = this.listQuery.platform
      if (this.listQuery.eventType) params.eventType = this.listQuery.eventType
      if (this.listQuery.status) params.status = this.listQuery.status
      if (this.listQuery.userId) params.userId = this.listQuery.userId
      if (this.listQuery.includeTest) params.includeTest = 'true'

      const response: any = await request({
        url: '/admin/subscription/events',
        method: 'get',
        params,
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      })

      const res = response.data || response
      if (res.success) {
        this.events = res.data
        this.total = res.pagination.total
        this.statistics = res.statistics.eventTypes || []
      }
    } catch (error) {
      console.error('Failed to load events:', error)
      this.$message.error('Failed to load subscription events')
    } finally {
      this.listLoading = false
    }
  }

  private handleFilter() {
    this.listQuery.page = 1
    this.loadEvents()
  }

  private handleRefresh() {
    this.loadEvents()
  }

  private handleSizeChange(val: number) {
    this.listQuery.limit = val
    this.loadEvents()
  }

  private handleCurrentChange(val: number) {
    this.listQuery.page = val
    this.loadEvents()
  }

  private showDetails(row: any) {
    this.selectedEvent = row
    this.detailsDialogVisible = true
  }

  private getDialogTitle(event: any): string {
    if (!event.event_type) return 'Event Details'
    return `${this.getEventTypeLabel(event.event_type)} - #${event.id}`
  }

  private getRowClassName({ row }: { row: any }): string {
    if (row.event_type === 'unknown') return 'unknown-row'
    if (!row.user_id) return 'no-user-row'
    return ''
  }

  // Event Type Icons
  private getEventTypeIcon(eventType: string): string {
    const iconMap: { [key: string]: string } = {
      purchase: 'el-icon-shopping-cart-2',
      renewal: 'el-icon-refresh-right',
      cancel: 'el-icon-close',
      expire: 'el-icon-time',
      refund: 'el-icon-money',
      renewal_failed: 'el-icon-warning',
      grace_period: 'el-icon-timer',
      hold: 'el-icon-lock',
      pause: 'el-icon-video-pause',
      reactivate: 'el-icon-video-play',
      preference_change: 'el-icon-setting',
      status_change: 'el-icon-switch-button',
      unknown: 'el-icon-question'
    }
    return iconMap[eventType] || 'el-icon-info'
  }

  private getEventTypeColor(eventType: string): string {
    const colorMap: { [key: string]: string } = {
      purchase: '#67c23a',
      renewal: '#67c23a',
      cancel: '#f56c6c',
      expire: '#e6a23c',
      refund: '#f56c6c',
      renewal_failed: '#f56c6c',
      grace_period: '#e6a23c',
      hold: '#e6a23c',
      pause: '#909399',
      reactivate: '#67c23a',
      unknown: '#909399'
    }
    return colorMap[eventType] || '#909399'
  }

  private getEventTypeTag(eventType: string): string {
    const typeMap: { [key: string]: string } = {
      purchase: 'success',
      renewal: 'success',
      cancel: 'danger',
      expire: 'warning',
      refund: 'danger',
      renewal_failed: 'danger',
      grace_period: 'warning',
      hold: 'warning',
      pause: 'info',
      reactivate: 'success',
      preference_change: 'info',
      status_change: 'info',
      unknown: 'info'
    }
    return typeMap[eventType] || 'info'
  }

  private getStatusIcon(status: string): string {
    const iconMap: { [key: string]: string } = {
      active: 'el-icon-circle-check',
      cancelled: 'el-icon-circle-close',
      expired: 'el-icon-time',
      in_grace_period: 'el-icon-timer',
      on_hold: 'el-icon-lock',
      paused: 'el-icon-video-pause',
      refunded: 'el-icon-money'
    }
    return iconMap[status] || 'el-icon-info'
  }

  private getStatusTag(status: string): string {
    const statusMap: { [key: string]: string } = {
      active: 'success',
      cancelled: 'danger',
      expired: 'warning',
      in_grace_period: 'warning',
      on_hold: 'warning',
      paused: 'info',
      refunded: 'danger'
    }
    return statusMap[status] || 'info'
  }

  // Human-readable event type labels
  private getEventTypeLabel(eventType: string): string {
    const labelMap: { [key: string]: string } = {
      purchase: 'New Purchase',
      renewal: 'Auto Renewal',
      cancel: 'Cancelled',
      expire: 'Expired',
      refund: 'Refunded',
      renewal_failed: 'Renewal Failed',
      grace_period: 'Grace Period',
      hold: 'On Hold',
      pause: 'Paused',
      reactivate: 'Reactivated',
      preference_change: 'Preference Change',
      status_change: 'Status Change',
      unknown: 'Unclassified'
    }
    return labelMap[eventType] || eventType || 'Unknown'
  }

  // Event subtype labels
  private getEventSubtypeLabel(subtype: string): string {
    const labelMap: { [key: string]: string } = {
      new_purchase: 'First purchase',
      auto_renewed: 'Automatically renewed',
      recovered_from_hold: 'Recovered from billing hold',
      recovered_from_billing_retry: 'Recovered after retry',
      user_cancelled: 'User cancelled',
      billing_error: 'Billing error',
      user_restarted: 'User restarted',
      user_accepted: 'User accepted',
      billing_deferred: 'Billing deferred',
      user_paused: 'User paused',
      user_modified: 'User modified',
      revoked_by_google: 'Revoked by Google',
      revoked_by_apple: 'Revoked by Apple',
      subscription_ended: 'Subscription ended',
      auto_renew_toggled: 'Auto-renew toggled'
    }
    return labelMap[subtype] || subtype || ''
  }

  // Human-readable status labels
  private getStatusLabel(status: string): string {
    const labelMap: { [key: string]: string } = {
      active: 'Active',
      cancelled: 'Cancelled',
      expired: 'Expired',
      in_grace_period: 'Grace Period',
      on_hold: 'On Hold',
      paused: 'Paused',
      refunded: 'Refunded'
    }
    return labelMap[status] || status || 'Unknown'
  }

  // Subscription period labels
  private getSubscriptionPeriodLabel(period: string): string {
    const labelMap: { [key: string]: string } = {
      monthly: 'Monthly',
      yearly: 'Yearly',
      'monthly-consult': 'Monthly',
      'additional-consult': 'One-time'
    }
    return labelMap[period] || period || ''
  }

  // Subscription period tag colors
  private getSubscriptionPeriodTag(period: string): string {
    const tagMap: { [key: string]: string } = {
      monthly: '',
      yearly: 'warning',
      'monthly-consult': 'info',
      'additional-consult': 'info'
    }
    return tagMap[period] || ''
  }

  // Format price with currency
  private formatPrice(amount: number | string, currency: string): string {
    if (!amount) return ''
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    const currencySymbols: { [key: string]: string } = {
      USD: '$',
      EUR: '\u20AC',
      TRY: '\u20BA',
      GBP: '\u00A3'
    }
    const symbol = currencySymbols[currency] || currency || ''
    return `${symbol}${numAmount.toFixed(2)}`
  }

  // Cancellation reason labels
  private getCancellationReasonLabel(reason: string | null): string {
    if (!reason) return '-'
    const labelMap: { [key: string]: string } = {
      user_cancelled: 'User Cancelled',
      billing_error: 'Billing Error',
      developer_cancelled: 'Developer Cancelled',
      system_cancelled: 'System Cancelled',
      price_increase: 'Price Increase',
      product_not_available: 'Product Not Available'
    }
    return labelMap[reason] || reason
  }

  // Get product display name
  private getProductDisplayName(row: any): string {
    if (row.product_name) return row.product_name
    // Extract readable name from product_id
    const productId = row.product_id || ''
    if (productId.includes('monthly')) return 'Smart Map Monthly'
    if (productId.includes('yearly') || productId.includes('annual')) return 'Smart Map Yearly'
    if (productId.includes('consult')) return 'Consult Package'
    return productId.split('.').pop() || productId
  }

  // Truncate user ID for display
  private truncateUserId(userId: string): string {
    if (!userId) return ''
    if (userId.length <= 12) return userId
    return `${userId.substring(0, 8)}...${userId.substring(userId.length - 4)}`
  }

  // Truncate notes for display
  private truncateNotes(notes: string): string {
    if (!notes) return ''
    if (notes.length <= 50) return notes
    return notes.substring(0, 47) + '...'
  }

  // Check if event is from sandbox (iOS: 5-minute renewals)
  private isSandboxEvent(row: any): boolean {
    if (row.platform !== 'ios') return false
    if (!row.event_date || !row.expires_date) return false

    const eventDate = new Date(row.event_date)
    const expiresDate = new Date(row.expires_date)
    const diffMinutes = (expiresDate.getTime() - eventDate.getTime()) / (1000 * 60)

    // Sandbox monthly subscriptions expire in ~5 minutes
    return diffMinutes > 0 && diffMinutes < 10
  }

  // Check if subscription is expiring soon (within 7 days)
  private isExpiringSoon(expiresDate: string): boolean {
    if (!expiresDate) return false
    const expires = new Date(expiresDate)
    const now = new Date()
    const diffDays = (expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    return diffDays > 0 && diffDays < 7
  }

  private async openUserDetail(userId: string) {
    if (!userId) return
    this.userDetailVisible = true
    this.userDetailLoading = true
    this.userDetailData = {}
    this.userEntitlements = []
    this.userDetailTab = 'basic'
    try {
      const { data } = await getUserById(userId)
      this.userDetailData = data.data || data || {}
    } catch (error) {
      console.error('Failed to load user detail:', error)
      this.$message.error('Failed to load user details')
    } finally {
      this.userDetailLoading = false
    }
    // Load entitlements in parallel
    this.entitlementsLoading = true
    try {
      const { data } = await getUserEntitlements(userId, true)
      this.userEntitlements = data.data || data || []
    } catch (error) {
      console.error('Failed to load entitlements:', error)
    } finally {
      this.entitlementsLoading = false
    }
  }

  private formatDetailDate(dateStr: string | Date | null | undefined): string {
    if (!dateStr) return '-'
    const date = new Date(dateStr as string)
    if (isNaN(date.getTime())) return '-'
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    return `${day}.${month}.${year}`
  }
}
